import React from 'react';
import { useIntl } from 'react-intl';
import { QueryClient, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { PermissionSet, User, UserStatus } from 'api/models';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import {
  deactivateMemberAction,
  reactivateMemberAction,
  reinviteMembersAction,
} from 'data/members/actions';
import { MembersResponse } from 'data/members/api';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { SUBSCRIPTION_KEY, useReactivateMemberMutation } from 'queries/billing';
import { ALL_MEMBERS_QUERY_KEY } from 'queries/members';
import { logger } from 'services';
import { updateMemberStatus } from 'utils/idb-utils';

const DecativeUserConfirmDesc = styled.div`
  padding-top: var(--spacing-05);
`;
const updateUserStatus = (
  userId: number,
  updatedUser: Partial<User>,
  queryClient: QueryClient,
) => {
  queryClient.setQueryData<MembersResponse>(
    ALL_MEMBERS_QUERY_KEY,
    (previous) => {
      return {
        ...previous,
        members:
          previous?.members.map((member) => {
            return {
              ...member,
              user:
                member.user.userId === userId
                  ? { ...member.user, ...updatedUser }
                  : member.user,
            };
          }) ?? [],
      };
    },
  );

  if (updatedUser?.userStatus) {
    updateMemberStatus(userId, updatedUser?.userStatus).catch((err) => {
      logger.error(
        `Error occured while updating user:${userId} to status: ${updatedUser.userStatus}  in indexedDB`,
        err,
      );
    });
  }
};

export const useToggleActivation = (userId, userIsActive, userName) => {
  const isBillingEnabled = useBillingFeatureFlag();
  const queryClient = useQueryClient();
  const {
    mutateAsync: reactivateMember,
    isLoading: isReactivating,
    isSuccess: isReactivationSuccess,
    isError: isReactivationError,
  } = useReactivateMemberMutation();

  const intl = useIntl();
  const dispatch = useDispatch();
  const successTitle = useTranslation('success');
  const failureTitle = useTranslation('failure');
  const deactivationSuccess = useTranslation(
    'settings.team_members.deactivation.success',
  );
  const deactivationError = useTranslation(
    'settings.team_members.deactivation.error',
  );
  const reactivationSuccess = useTranslation(
    'settings.team_members.reactivation.success',
  );
  const reactivationError = useTranslation(
    'settings.team_members.reactivation.error',
  );
  const confirmDesc = intl.formatMessage(
    {
      id: 'settings.team_members.confirmation.description',
    },
    {
      userName: <strong>{userName}</strong>,
    },
  );
  const confirmText = intl.formatMessage(
    {
      id: 'settings.team_members.confirmation.text',
    },
    {
      userName: <strong>{userName}</strong>,
    },
  );
  const confirmTitle = useTranslation(
    'settings.team_members.confirmation.title',
  );

  const okText = useTranslation(
    'settings.team_members.confirmation.cta.submit',
  );

  const refetchSubscription = () => {
    /**
     * refetching subscription to get the latest available license count.
     */
    if (isBillingEnabled) {
      queryClient.invalidateQueries(SUBSCRIPTION_KEY);
    }
  };

  const deactivateUser = () => {
    updateUserStatus(userId, { userStatus: UserStatus.Inactive }, queryClient);

    dispatch(
      deactivateMemberAction({
        userId,
        handleSuccess() {
          toastService({
            title: successTitle,
            message: deactivationSuccess,
          }).success();
          refetchSubscription();
        },
        handleFailure() {
          toastService({
            title: failureTitle,
            message: deactivationError,
          }).error();
          updateUserStatus(
            userId,
            { userStatus: UserStatus.Active },
            queryClient,
          );
        },
      }),
    );

    return Promise.resolve();
  };
  const reactivateUser = async () => {
    updateUserStatus(userId, { userStatus: UserStatus.Active }, queryClient);

    if (isBillingEnabled) {
      try {
        await reactivateMember(userId);
        toastService({
          title: successTitle,
          message: reactivationSuccess,
        }).success();
        refetchSubscription();
      } catch {
        toastService({
          title: failureTitle,
          message: reactivationError,
        }).error();
        updateUserStatus(
          userId,
          { userStatus: UserStatus.Inactive },
          queryClient,
        );
      }

      return;
    }
    /**
     * following dispatch code will be dropped post billing release.
     */
    dispatch(
      reactivateMemberAction({
        userId,
        handleSuccess() {
          toastService({
            title: successTitle,
            message: reactivationSuccess,
          }).success();
          refetchSubscription();
        },
        handleFailure() {
          toastService({
            title: failureTitle,
            message: reactivationError,
          }).error();
          updateUserStatus(
            userId,
            { userStatus: UserStatus.Inactive },
            queryClient,
          );
        },
      }),
    );
  };
  const DeleteConfirmation = useDeleteConfirmModal({
    title: confirmTitle,
    content: (
      <>
        {confirmText}
        <DecativeUserConfirmDesc>{confirmDesc}</DecativeUserConfirmDesc>
      </>
    ),
    okText,
    onOk: () => {
      window.setTimeout(() => {
        deactivateUser();
      }, 30);
    },
  });
  const onToggleActivation = () => {
    if (userIsActive) {
      DeleteConfirmation();
    } else {
      reactivateUser();
    }
  };

  return {
    onToggleActivation,
    isReactivating,
    isReactivationSuccess,
    isReactivationError,
  };
};

export const useResendAction = (
  reinvitingUser: User,
  permissionSet: PermissionSet,
) => {
  const { emailId, role, defaultCapacity, capacityMinutes } = reinvitingUser;
  const intl = useIntl();
  const dispatch = useDispatch();
  const resend = useTranslation('resend');
  const resendConfirmTitle = useTranslation('invitation.resend.confirm.title');
  const resendConfirmSuccessTitle = useTranslation('invitation.success.title');
  const resendConfirmSuccessContent = useTranslation(
    'invitation.success.content',
  );
  const resendConfirmErrorContent = useTranslation(
    'invitation.resend.confirm.error.content',
  );
  const errorTitle = useTranslation('failure');
  const resendInvite = () => {
    if (emailId && role) {
      const payload = [
        {
          user: {
            emailId,
            role,
            defaultCapacity,
            capacityMinutes,
          },
          permissionSet,
        },
      ];

      confirm({
        title: resendConfirmTitle,
        content: intl.formatMessage(
          { id: 'invitation.resend.confirm.content' },
          {
            user: <b>{reinvitingUser.emailId}</b>,
          },
        ),
        okText: resend,
        onOk() {
          dispatch(
            reinviteMembersAction({
              data: payload,
              handleSuccess() {
                toastService({
                  title: resendConfirmSuccessTitle,
                  message: resendConfirmSuccessContent,
                }).success();
              },
              handleFailure() {
                toastService({
                  title: errorTitle,
                  message: resendConfirmErrorContent,
                }).error();
              },
            }),
          );
        },
      });
    }
  };

  return resendInvite;
};
